import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api';

const FEATURE_LABELS = {
  classic_search: 'Buscador clásico con IA (fuzzy, sinónimos, multiidioma)',
  chat: 'Chat con IA + agente humano',
  xml_feed: 'Feed XML de catálogo',
  analytics: 'Analítica de búsquedas',
  widget_customization: 'Widget personalizable (colores, textos, marca)',
};
const FEATURE_ORDER = ['classic_search', 'ai_included', 'chat', 'xml_feed', 'analytics', 'widget_customization'];

function buildFeatureList(features = {}) {
  const list = [];
  for (const key of FEATURE_ORDER) {
    if (key === 'ai_included') {
      if (features.ai_included) {
        const limit = features.ai_searches_limit || 0;
        list.push(limit > 0
          ? `IA que reordena resultados — hasta ${limit.toLocaleString('es-ES')} búsquedas/mes`
          : 'IA que reordena resultados — sin límite mensual');
      }
    } else if (features[key]) {
      list.push(FEATURE_LABELS[key]);
    }
  }
  return list;
}

function PlanCard({ plan, highlight, savingsNote }) {
  const features = buildFeatureList(plan.features);
  return (
    <div
      data-testid={`pricing-plan-card-${plan.plan_key}`}
      className={`relative rounded-3xl border p-7 flex flex-col ${
        highlight ? 'border-[#00E5FF]/50 bg-[#0F0F11] shadow-[0_0_40px_rgba(0,229,255,0.12)]' : 'border-white/10 bg-[#0F0F11]'
      }`}
    >
      {highlight && (
        <span className="absolute -top-3 left-7 rounded-full bg-[#00E5FF] text-black text-xs font-semibold px-3 py-1">
          Más completo
        </span>
      )}
      <h3 className="text-white font-bold text-xl mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
      <p className="text-zinc-500 text-sm mb-6 leading-relaxed min-h-[40px]">{plan.description}</p>

      <div className="mb-6">
        <span className="text-4xl font-black text-white">{plan.price}€</span>
        <span className="text-zinc-500 text-sm"> /mes</span>
        {savingsNote && (
          <p data-testid="pricing-plan-savings-note" className="text-xs text-[#00E5FF] mt-1.5">{savingsNote}</p>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
            <Check size={16} className="text-[#00E5FF] mt-0.5 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <Link
        to="/#demo"
        data-testid={`pricing-plan-cta-${plan.plan_key}`}
        className={`text-center rounded-full font-semibold text-sm px-5 py-3 transition-colors ${
          highlight ? 'bg-[#00E5FF] text-black hover:bg-[#00B8CC]' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
        }`}
      >
        Solicita una demo
      </Link>
    </div>
  );
}

export function PricingGrid() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/billing/plans')
      .then((r) => setPlans((r.data.plans || []).filter((p) => !p.is_addon)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const searchAi = plans.find((p) => p.plan_key === 'search_ai');
  const chatPlan = plans.find((p) => p.plan_key === 'chat');
  const fullPlan = plans.find((p) => p.plan_key === 'full');
  let savings = null;
  if (searchAi && chatPlan && fullPlan) {
    const diff = searchAi.price + chatPlan.price - fullPlan.price;
    if (diff > 0) savings = diff;
  }

  if (loading) {
    return (
      <section className="py-10 px-6 flex justify-center">
        <Loader2 size={22} className="text-zinc-500 animate-spin" data-testid="pricing-grid-loading" />
      </section>
    );
  }

  return (
    <section data-testid="pricing-grid-section" className="py-10 md:py-16 px-6 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard
            key={plan.plan_key}
            plan={plan}
            highlight={plan.plan_key === 'full'}
            savingsNote={plan.plan_key === 'full' && savings ? `Ahorra ${savings}€/mes vs. comprar Search AI + Chat por separado` : null}
          />
        ))}
      </div>
    </section>
  );
}
